/**
 * FounderNav.jsx — Founder Navigation System
 * 
 * Visible ONLY when Founder mode is active.
 * Provides absolute access to all surfaces.
 * 
 * Founder exists ABOVE access control.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Eye, 
  EyeOff,
  Lock,
  Unlock,
  FileText,
  Settings,
  Map,
  DollarSign,
  FlaskConical,
  Calculator,
  Layers,
  Home,
  ChevronDown,
  Landmark,
  Zap,
  Globe,
  Brain,
  FileCheck,
  Train,
  Sparkles
} from 'lucide-react';
import { useFounder } from '../../contexts/FounderContext';

const FOUNDER_NAV_ITEMS = [
  { label: 'Dashboard', to: '/founder', icon: Shield },
  { label: 'Intent Records', to: '/intent-records', icon: FileText, gated: true },
  { label: 'Preparedness', to: '/preparedness', icon: DollarSign, gated: true },
  { label: 'CBDC', to: '/cbdc', icon: Landmark },
  { label: 'Pricing', to: '/pricing', icon: Calculator },
  { label: 'Docs', to: '/how-granullo-works', icon: Map },
  { label: 'Dev Settings', to: '/dev-settings', icon: Settings },
];

const PRIMITIVES_NAV_ITEMS = [
  { label: 'Create', to: '/create', icon: Sparkles },
  { label: 'Boundaries', to: '/boundaries', icon: Shield },
  { label: 'Triggers', to: '/triggers', icon: Zap },
  { label: 'Network', to: '/trusted-network', icon: Globe },
  { label: 'Twin', to: '/twin-memory', icon: Brain },
  { label: 'Passport', to: '/compliance-passport', icon: FileCheck },
  { label: 'Rails', to: '/rail-readiness', icon: Train },
  { label: 'Labs', to: '/labs', icon: FlaskConical },
];

export const FounderNav = () => {
  const { isFounder, viewMode, setViewMode, founderOverride, setFounderOverride } = useFounder();
  const location = useLocation();
  
  if (!isFounder) return null;
  
  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-[100] bg-violet-950 border-b border-violet-800 text-white"
      data-testid="founder-nav"
    >
      <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
        {/* Left: Founder badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-2 py-1 rounded bg-violet-800">
            <Shield className="w-4 h-4 text-violet-300" />
            <span className="text-[11px] font-bold uppercase tracking-wide">Founder</span>
          </div>
          
          {/* Quick nav */}
          <div className="flex items-center gap-1 ml-4">
            {FOUNDER_NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] transition-all ${
                    isActive 
                      ? 'bg-violet-700 text-white' 
                      : 'text-violet-300 hover:text-white hover:bg-violet-800'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>
                  {item.gated && <Lock className="w-2.5 h-2.5 opacity-50" />}
                </Link>
              );
            })}
            {/* Primitives divider */}
            <div className="w-px h-4 mx-1" style={{ background: 'rgba(139,92,246,0.3)' }} />
            {PRIMITIVES_NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded text-[10px] transition-all ${
                    isActive 
                      ? 'bg-emerald-800 text-emerald-200' 
                      : 'text-violet-400 hover:text-emerald-300 hover:bg-violet-800'
                  }`}
                  data-testid={`founder-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        
        {/* Right: View mode controls */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-violet-400 mr-2">View as:</span>
          
          <button
            onClick={() => { setViewMode('public'); setFounderOverride(false); }}
            className={`px-2 py-1 rounded text-[10px] transition-all ${
              viewMode === 'public' 
                ? 'bg-violet-600 text-white' 
                : 'text-violet-400 hover:text-white'
            }`}
          >
            <Eye className="w-3 h-3 inline mr-1" />
            Public
          </button>
          
          <button
            onClick={() => { setViewMode('demo'); setFounderOverride(false); }}
            className={`px-2 py-1 rounded text-[10px] transition-all ${
              viewMode === 'demo' 
                ? 'bg-violet-600 text-white' 
                : 'text-violet-400 hover:text-white'
            }`}
          >
            <EyeOff className="w-3 h-3 inline mr-1" />
            Demo
          </button>
          
          <button
            onClick={() => { setViewMode('founder'); setFounderOverride(true); }}
            className={`px-2 py-1 rounded text-[10px] transition-all ${
              viewMode === 'founder' 
                ? 'bg-emerald-600 text-white' 
                : 'text-violet-400 hover:text-white'
            }`}
          >
            <Unlock className="w-3 h-3 inline mr-1" />
            Override
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FounderNav;
